import { Button, HSpacer, IconButton, Search, VSpacer } from "@/components/DesignSystem";
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import { ModalBottomSheet } from '@/components/shared/ModalBottomSheet/ModalBottomSheet';
import { ProductListContent } from '@/components/shared/SubwalletBalance/ProductListContent';
import { QueryKeys } from '@/constants/QueryKeys';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSearch } from '@/hooks/useSearch';
import { SubwalletApi } from '@/utilities/api/SubwalletApi';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { Divider } from '@mui/material';
import { Stack } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';

interface SubwalletProductsBottomSheetProps {
  accessToken?: string,
  brandColor?: string,
  onClose: () => void,
  subwalletId?: string,
}

export const SubwalletProductsBottomSheet = ({
  accessToken,
  brandColor,
  onClose,
  subwalletId,
}: SubwalletProductsBottomSheetProps) => {
  const { debouncedSearch, search, setSearch } = useSearch();
  const { isMobile } = useMediaQuery();
  const { t } = useTranslation();
  const brandColorBg = brandColor || 'primary.main';

  const {
    data: subwalletProducts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    [QueryKeys.LIST_REDEEMABLE_PRODUCTS, debouncedSearch, subwalletId],
    async ({ pageParam = 0 }) => SubwalletApi.listRedeemableProducts(
      subwalletId!,
      {
        accessToken,
        page: pageParam,
        limit: 20,
        search: debouncedSearch || undefined,
      },
    ),
    {
      enabled: !!subwalletId,
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.lastPage ? lastPage.page + 1 : undefined,
      keepPreviousData: true,
    },
  );

  const products = subwalletProducts?.pages?.flatMap(({ data }) => data) ?? [];
  const productsCount = subwalletProducts?.pages?.[0]?.total ?? 0;

  return (
    <ModalBottomSheet
      bottomSheetContainerSx={{ overflow: 'hidden' }}
      footerSpacerSize="1"
      hideBottomCloseButton
      hideTopCloseButton
      onClose={onClose}
      open
      showFooter={false}
      showTopButton={false}
      testID="redeemable-products-modal"
      title={(
        <>
          <Stack alignItems="center" direction="row" width="100%">
            {isMobile ? (
              <IconButton
                onClick={onClose}
                sx={{ color: brandColorBg }}
                testID="redeemable-modal-back-button"
              >
                <ArrowBack />
              </IconButton>
            ) : (
              <Button
                onClick={onClose}
                startIcon={<ArrowBack />}
                sx={{ px: '12px', color: brandColorBg }}
                testID="redeemable-modal-back-button"
                variant="text"
              >
                {t($ => $.common.actions.back)}
              </Button>
            )}
            {isMobile && (
              <>
                <HSpacer size="2" />
                <Search
                  fullWidth
                  onChangeText={setSearch}
                  placeholder={t($ => $.common.search.searchRedeemableProducts)}
                  testID="redeemable-products-search"
                  value={search}
                />
                <HSpacer size="3" />
              </>
            )}
          </Stack>
          {isMobile && (
            <>
              <VSpacer size="6" />
              <Divider sx={{ width: '100%' }} />
            </>
          )}
        </>
      )}
    >
      <InfiniteScroll
        fetchMoreItems={fetchNextPage}
        hasMore={hasNextPage ?? true}
        height="80vh"
        isLoading={isFetchingNextPage}
      >
        <ProductListContent
          isMobile={isMobile}
          products={products}
          productsCount={productsCount}
          search={search}
          setSearch={setSearch}
        />
      </InfiniteScroll>
    </ModalBottomSheet>
  );
};
